import React, { useRef, useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { useTranslation } from '../../i18n';
import { renderAvatar } from '../../utils/avatarUtils';
import { LiveAnswer, formatPairMatchingAnswer } from './LiveAnswers';
import { FLIP_DELAY } from '../../constants/gameConstants';
import { FlipRollingInteger } from './FlipRollingInteger';

interface Player {
  id: string;
  name: string;
  score: number;
  avatar?: string;
}

interface StandingsProps {
  /** Array of players to display in standings */
  players: Player[];
  /** Whether this is mobile view */
  isMobile?: boolean;
  /** Theme accent color */
  accent?: string;
  /** Maximum number of players to show */
  maxPlayers?: number;
  /** Whether scores should be updated (set to true when question-result is received) */
  shouldUpdateScores?: boolean;
  /** Current question ID - when this changes, scores are frozen */
  questionId?: string;
  /** Points gained per player (for animation) */
  pointsGained?: Map<string, number>;
  /** Whether to show points gained animation */
  showPointsGained?: Map<string, boolean>;
  /** Live answers to show inline (given answer per player); when set, show given-answer column */
  liveAnswers?: LiveAnswer[];
  /** Total players (used with liveAnswers to know when everyone has answered) */
  totalPlayers?: number;
  /** When false, hide the given-answer column (e.g. after showing green points for 5s) */
  showGivenAnswers?: boolean;
  /** Callback to register score element refs (for backward compatibility) */
  onScoreRef?: (playerId: string, element: HTMLSpanElement | null) => void;
  /** Visual style variant */
  variant?: 'default' | 'light-card';
}

const FLIP_ANIMATION_MS = 1200;
type PackVariant = 'blitz' | 'party' | 'team' | 'family' | 'coop' | 'new';

/** Row tints rotate per player — chosen for variety within each pack. */
const PACK_ROW_PALETTES: Record<PackVariant, string[]> = {
  family: ['#15803D', '#B91C1C', '#6D28D9', '#1D4ED8', '#0E7490', '#BE185D', '#C2410C', '#4338CA', '#047857'],
  team: ['#1E40AF', '#312E81', '#3730A3', '#1D4ED8', '#0369A1', '#4C1D95', '#5B21B6', '#0F766E', '#1E3A8A'],
  blitz: ['#991B1B', '#B91C1C', '#DC2626', '#C2410C', '#9A3412', '#BE123C', '#E11D48', '#7F1D1D', '#EA580C'],
  party: ['#86198F', '#A21CAF', '#BE185D', '#7C3AED', '#6D28D9', '#C026D3', '#DB2777', '#9333EA', '#9D174D'],
  coop: ['#166534', '#1D4ED8', '#0E7490', '#4D7C0F', '#0369A1', '#5B21B6', '#047857', '#1E40AF', '#0F766E'],
  new: ['#BE185D', '#DB2777', '#9D174D', '#E11D48', '#A21CAF', '#C026D3', '#F43F5E', '#86198F', '#EC4899'],
};

function hexToRgba(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '');
  const value = normalized.length === 3
    ? normalized.split('').map((c) => c + c).join('')
    : normalized;

  const int = Number.parseInt(value, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function getActivePackVariant(): PackVariant {
  if (typeof document === 'undefined') return 'party';
  const raw = document.documentElement.dataset.pack || document.documentElement.dataset.theme;
  switch (raw) {
    case 'blitz':
    case 'party':
    case 'team':
    case 'family':
    case 'coop':
    case 'new':
      return raw;
    default:
      return 'party';
  }
}

/**
 * Reusable Standings component that displays player rankings with smooth animations.
 * Players smoothly animate to new positions when scores change and they're re-sorted.
 */
export const Standings: React.FC<StandingsProps> = ({
  players,
  isMobile = false,
  accent = '#F59E0B',
  maxPlayers,
  shouldUpdateScores = false,
  questionId,
  pointsGained = new Map(),
  showPointsGained = new Map(),
  liveAnswers,
  totalPlayers,
  showGivenAnswers = true,
  onScoreRef,
  variant = 'default',
}) => {
  const { t } = useTranslation();
  const maxPlayersToShow = maxPlayers !== undefined ? maxPlayers : players.length;
  const isLightCard = variant === 'light-card';
  const cardBackground = isLightCard ? 'rgba(255, 255, 255, 0.10)' : undefined;
  const cardBorder = isLightCard ? '1px solid rgba(168, 85, 247, 0.18)' : undefined;
  const cardShadow = isLightCard ? '0 0 0 1px rgba(255, 255, 255, 0.08), 0 18px 40px rgba(37, 23, 66, 0.16), 0 0 24px rgba(168, 85, 247, 0.14)' : undefined;
  const headingColor = 'var(--text-primary)';
  const rowDivider = isLightCard ? '1px solid rgba(99, 102, 241, 0.10)' : '1px solid var(--border-light)';
  const nameColor = '#FFFFFF';
  const packVariant = getActivePackVariant();
  const rowPalette = PACK_ROW_PALETTES[packVariant];

  // Map playerId -> answer for inline given-answer column
  const answerByPlayerId = useMemo(() => {
    if (!liveAnswers || !liveAnswers.length) return new Map<string, string | number | undefined>();
    const map = new Map<string, string | number | undefined>();
    liveAnswers.forEach((a) => map.set(a.playerId, a.answer));
    return map;
  }, [liveAnswers]);

  // Track which player avatars should play "live answer added" zoom animation
  const [avatarZoomPlayerIds, setAvatarZoomPlayerIds] = useState<Set<string>>(new Set());
  const prevAnswerPlayerIdsRef = useRef<Set<string>>(new Set());

  // When live answers gain a new player, trigger avatar zoom for that player
  useEffect(() => {
    const currentIds = new Set(answerByPlayerId.keys());
    const prev = prevAnswerPlayerIdsRef.current;
    const added = Array.from(currentIds).filter((id) => !prev.has(id));
    prevAnswerPlayerIdsRef.current = currentIds;
    if (added.length > 0) {
      setAvatarZoomPlayerIds(new Set(added));
      const t = setTimeout(() => setAvatarZoomPlayerIds(new Set()), 500);
      return () => clearTimeout(t);
    }
  }, [answerByPlayerId]);
  
  // Track frozen scores and animation state
  const [frozenPlayers, setFrozenPlayers] = useState<Player[]>(players);
  const [isAnimating, setIsAnimating] = useState(false);
  /** Previous scores per player for airport flip animation (cleared after FLIP_ANIMATION_MS). */
  const [previousScores, setPreviousScores] = useState<Map<string, number>>(new Map());
  const previousQuestionIdRef = useRef<string | undefined>(questionId);
  const previousFrozenPlayersRef = useRef<Player[]>(players);
  const previousShouldUpdateScoresRef = useRef<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const oldPositionsRef = useRef<Map<string, { top: number; left: number }>>(new Map());

  const getRowSurface = (index: number) => {
    const color = rowPalette[index % rowPalette.length];
    const startAlpha = isLightCard ? (index === 0 ? 0.72 : 0.70) : (index === 0 ? 0.78 : 0.72);
    const endAlpha = isLightCard ? (index === 0 ? 0.86 : 0.89) : (index === 0 ? 0.90 : 0.93);

    return {
      color,
      background: `linear-gradient(135deg, ${hexToRgba(color, startAlpha)}, ${hexToRgba(color, endAlpha)})`,
      border: `1px solid ${hexToRgba(color, isLightCard ? 0.55 : 0.40)}`,
      boxShadow: `0 10px 22px ${hexToRgba(color, isLightCard ? 0.24 : 0.18)}, inset 0 1px 0 ${hexToRgba('#FFFFFF', 0.12)}`,
    };
  };

  // Handle score freezing and updates
  useEffect(() => {
    // If questionId changed, freeze current scores (new question started)
    if (questionId && questionId !== previousQuestionIdRef.current) {
      previousQuestionIdRef.current = questionId;
      setFrozenPlayers(players);
      previousFrozenPlayersRef.current = players;
      previousShouldUpdateScoresRef.current = false;
      return;
    }

    // If players list changed (new player joined mid-game), update frozenPlayers to include new players
    const playerIds = new Set(players.map((p) => p.id));
    const hasLeftPlayers = frozenPlayers.some((fp) => !playerIds.has(fp.id));
    const hasNewPlayers = players.some(p => !frozenPlayers.find(fp => fp.id === p.id));
    if (hasLeftPlayers && !isAnimating) {
      const pruned = frozenPlayers.filter((fp) => playerIds.has(fp.id));
      setFrozenPlayers(pruned);
      previousFrozenPlayersRef.current = pruned;
    } else if (hasNewPlayers && !isAnimating) {
      const updatedFrozenPlayers = players.map(player => {
        const existingFrozen = frozenPlayers.find(fp => fp.id === player.id);
        return existingFrozen || player;
      });
      setFrozenPlayers(updatedFrozenPlayers);
      previousFrozenPlayersRef.current = updatedFrozenPlayers;
    }

    // If shouldUpdateScores is true (question-result received), update scores with animation.
    // Also allow when players have higher scores (bonus-points-updated late submissions).
    const hasHigherScores = players.some(
      (p) => (frozenPlayers.find((fp) => fp.id === p.id)?.score ?? 0) < p.score
    );
    const shouldTriggerUpdate =
      shouldUpdateScores &&
      (!previousShouldUpdateScoresRef.current || hasHigherScores);

    if (shouldTriggerUpdate) {
      previousShouldUpdateScoresRef.current = true;
      // Capture old scores for airport flip animation (before we update frozenPlayers)
      const oldScores = new Map<string, number>();
      previousFrozenPlayersRef.current.forEach((p) => oldScores.set(p.id, p.score));
      setPreviousScores(oldScores);

      // Capture old positions BEFORE updating (First step of FLIP)
      setTimeout(() => {
        const oldPositions = new Map<string, { top: number; left: number }>();
        if (containerRef.current) {
          itemRefs.current.forEach((el, playerId) => {
            if (el && containerRef.current) {
              const rect = el.getBoundingClientRect();
              const containerRect = containerRef.current.getBoundingClientRect();
              oldPositions.set(playerId, {
                top: rect.top - containerRect.top,
                left: rect.left - containerRect.left,
              });
            }
          });
        }

        // Mark that we're starting the animation and store old positions
        setIsAnimating(true);
        oldPositionsRef.current = oldPositions;

        // Use requestAnimationFrame to ensure DOM is ready for smooth transition
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Update scores - this will trigger sorting animation
            setFrozenPlayers(players);
            previousFrozenPlayersRef.current = players;

            // After sorting animation completes (3s), reset animation state
            setTimeout(() => {
              setIsAnimating(false);
            }, 3000);
            // Clear previous scores after flip animation so next time we don't re-animate
            setTimeout(() => setPreviousScores(new Map()), FLIP_ANIMATION_MS + 200);
          });
        });
      }, FLIP_DELAY);
    } else if (!shouldUpdateScores) {
      // Reset the flag when shouldUpdateScores becomes false
      previousShouldUpdateScoresRef.current = false;
    } else if (!questionId) {
      // No questionId provided - always update (fallback for components that don't pass questionId)
      setFrozenPlayers(players);
      previousFrozenPlayersRef.current = players;
    }
  }, [players, questionId, shouldUpdateScores, frozenPlayers, isAnimating]);

  // Sort frozen players by score (highest first)
  const sortedPlayers = React.useMemo(() => {
    return [...frozenPlayers].sort((a, b) => {
      // Sort by score descending, then by id for stability
      if (b.score !== a.score) {
        return b.score - a.score;
      }
      return a.id.localeCompare(b.id);
    }).slice(0, maxPlayersToShow);
  }, [frozenPlayers, maxPlayersToShow]);

  // FLIP animation: track positions after reorder and animate from old to new positions
  useLayoutEffect(() => {
    if (!isAnimating || !containerRef.current || oldPositionsRef.current.size === 0) return;

    // Last: Get new positions after DOM update
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const oldPositions = oldPositionsRef.current;
        
        itemRefs.current.forEach((el, playerId) => {
          if (el && containerRef.current && oldPositions.has(playerId)) {
            const rect = el.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();
            const newTop = rect.top - containerRect.top;
            const newLeft = rect.left - containerRect.left;
            const oldPos = oldPositions.get(playerId)!;

            const deltaY = oldPos.top - newTop;
            const deltaX = oldPos.left - newLeft;

            // Only animate if there's a significant position change (more than 2px)
            if (Math.abs(deltaY) > 2 || Math.abs(deltaX) > 2) {
              // Invert: Apply transform to move element back to old position
              el.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(1.05)`;
              el.style.transition = 'none';
              el.style.opacity = '0.85';
              el.style.zIndex = '10';
              el.style.position = 'relative';

              // Force reflow to ensure transform is applied
              void el.offsetHeight;

              // Play: Animate to final position with smooth 3s transition
              requestAnimationFrame(() => {
                el.style.transition = 'transform 3s cubic-bezier(0.4, 0, 0.2, 1), opacity 3s ease-in-out';
                el.style.transform = 'translate(0, 0) scale(1)';
                el.style.opacity = '1';
                
                // Reset z-index and position after animation completes
                setTimeout(() => {
                  el.style.zIndex = '';
                  el.style.position = '';
                }, 3000);
              });
            } else {
              // Reset any transforms if no significant movement
              el.style.transform = '';
              el.style.transition = '';
              el.style.opacity = '';
              el.style.zIndex = '';
              el.style.position = '';
            }
          }
        });
        
        // Clear old positions after animation starts
        oldPositionsRef.current = new Map();
      });
    });
  }, [sortedPlayers, isAnimating]);

  return (
    <div 
      className="standings-container"
      data-testid="standings-container"
      style={{
        padding: isMobile ? '0.75rem' : '1rem',
        flex: isMobile ? 1 : undefined,
        background: cardBackground,
        border: cardBorder,
        boxShadow: cardShadow,
        borderRadius: isLightCard ? '24px' : undefined,
      }}
    >
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateX(-10px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes pointsGainedPop {
          0% { opacity: 0; transform: scale(0.3); }
          50% { transform: scale(1.25); }
          100% { opacity: 1; transform: scale(1); }
        }
        
        @keyframes avatarLiveAnswerPop {
          0% { transform: scale(1); }
          50% { transform: scale(1.6); }
          100% { transform: scale(1); }
        }
        
        .standings-avatar-zoom {
          display: inline-block;
          animation: avatarLiveAnswerPop 0.4s ease-out;
        }
        
        .standings-points-gained-pop {
          display: inline-block;
          animation: pointsGainedPop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .standings-score {
          color: var(--color-secondary) !important;
          text-shadow:
            0 0 6px rgba(var(--color-secondary-rgb), 1),
            0 0 14px rgba(var(--color-secondary-rgb), 0.9),
            0 0 28px rgba(var(--color-secondary-rgb), 0.55),
            0 2px 6px rgba(0, 0, 0, 0.55) !important;
        }

        .standings-points-gained {
          color: var(--color-secondary) !important;
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
          text-shadow:
            0 0 6px rgba(var(--color-secondary-rgb), 1),
            0 0 14px rgba(var(--color-secondary-rgb), 0.9),
            0 0 28px rgba(var(--color-secondary-rgb), 0.55),
            0 2px 6px rgba(0, 0, 0, 0.55) !important;
        }
        
        .standings-list-container {
          contain: layout style paint;
        }
        
        .standings-player-item {
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          perspective: 1000px;
          -webkit-perspective: 1000px;
          transform: translateZ(0);
          -webkit-transform: translateZ(0);
        }
        
        .standings-container {
          padding: 0.5rem 0 0.5rem 0.5rem;
        }
        
        @media (min-width: 769px) {
          .standings-container {
            padding: 0.65rem 0 0.65rem 0.65rem;
          }
        }
        
        @media (min-width: 1025px) {
          .standings-container {
            padding: 0.75rem 0 0.75rem 0.75rem;
          }
        }
        
        @media (min-width: 1440px) {
          .standings-container {
            padding: 0.85rem 0 0.85rem 0.85rem;
          }
        }
        
        .standings-heading {
          font-size: 0.85rem;
          margin: 0;
          text-align: center;
        }
        
        @media (min-width: 769px) {
          .standings-heading {
            font-size: 1rem;
            margin: 0;
          }
        }
        
        @media (min-width: 1025px) {
          .standings-heading {
            font-size: 1.2rem;
            margin-bottom: 0.8rem;
          }
        }
        
        @media (min-width: 1440px) {
          .standings-heading {
            font-size: 1.6rem;
            margin-bottom: 1rem;
          }
        }
        
        .standings-list-container:not(.mobile) .standings-rank {
          font-size: 0.9rem;
          width: 1.5rem;
        }
        
        .standings-list-container:not(.mobile) .standings-avatar {
          font-size: 1.2rem;
        }
        
        .standings-list-container:not(.mobile) .standings-avatar img {
          width: 1.2rem;
          height: 1.2rem;
        }
        
        .standings-list-container:not(.mobile) .standings-name {
          font-size: 0.9rem;
        }
        
        .standings-list-container:not(.mobile) .standings-points-gained {
          font-size: 0.85rem;
        }
        
        .standings-list-container:not(.mobile) .standings-score {
          font-size: 0.95rem;
        }
        
        .standings-list-container:not(.mobile) .standings-given-answer {
          font-size: 0.95rem;
        }
        
        @media (min-width: 769px) {
          .standings-list-container:not(.mobile) .standings-rank {
            font-size: 1rem;
            width: 1.8rem;
          }
          
          .standings-list-container:not(.mobile) .standings-avatar {
            font-size: 1.3rem;
          }
          
          .standings-list-container:not(.mobile) .standings-avatar img {
            width: 1.3rem;
            height: 1.3rem;
          }
          
          .standings-list-container:not(.mobile) .standings-name {
            font-size: 1rem;
          }
          
          .standings-list-container:not(.mobile) .standings-points-gained {
            font-size: 0.9rem;
          }
          
          .standings-list-container:not(.mobile) .standings-score {
            font-size: 1.05rem;
          }
          
          .standings-list-container:not(.mobile) .standings-given-answer {
            font-size: 1.05rem;
          }
        }
        
        @media (min-width: 1025px) {
          .standings-list-container:not(.mobile) .standings-rank {
            font-size: 1.1rem;
            width: 2rem;
          }
          
          .standings-list-container:not(.mobile) .standings-avatar {
            font-size: 1.4rem;
          }
          
          .standings-list-container:not(.mobile) .standings-avatar img {
            width: 1.4rem;
            height: 1.4rem;
          }
          
          .standings-list-container:not(.mobile) .standings-name {
            font-size: 1.1rem;
          }
          
          .standings-list-container:not(.mobile) .standings-points-gained {
            font-size: 1rem;
          }
          
          .standings-list-container:not(.mobile) .standings-score {
            font-size: 1.15rem;
          }
          
          .standings-list-container:not(.mobile) .standings-given-answer {
            font-size: 1.1rem;
          }
        }
        
        @media (min-width: 1440px) {
          .standings-list-container:not(.mobile) .standings-rank {
            font-size: 1.5rem;
            width: 2.5rem;
          }
          
          .standings-list-container:not(.mobile) .standings-avatar {
            font-size: 1.9rem;
          }
          
          .standings-list-container:not(.mobile) .standings-avatar img {
            width: 1.9rem;
            height: 1.9rem;
          }
          
          .standings-list-container:not(.mobile) .standings-name {
            font-size: 1.5rem;
          }
          
          .standings-list-container:not(.mobile) .standings-points-gained {
            font-size: 1.3rem;
          }
          
          .standings-list-container:not(.mobile) .standings-score {
            font-size: 1.6rem;
          }
          
          .standings-list-container:not(.mobile) .standings-given-answer {
            font-size: 1.25rem;
          }
          
          .standings-list-container:not(.mobile) .standings-player-item-desktop {
            padding: 0.9rem 1rem;
            gap: 0.7rem;
          }
        }
        
        @media (min-width: 1025px) {
          .standings-list-container:not(.mobile) .standings-player-item-desktop {
            padding: 0.8rem 0.95rem;
            gap: 0.6rem;
          }
        }
        
        @media (prefers-reduced-motion: no-preference) {
          .standings-list-container:not(.mobile) .standings-player-item {
            transition: transform 3s cubic-bezier(0.4, 0, 0.2, 1),
                        opacity 0.3s ease-in-out,
                        background-color 0.3s ease-in-out,
                        border-color 0.3s ease-in-out,
                        scale 3s ease-in-out;
          }
          
          .standings-list-container.mobile .standings-player-item {
            transition: transform 3s cubic-bezier(0.4, 0, 0.2, 1),
                        opacity 0.3s ease-in-out,
                        background-color 0.3s ease-in-out,
                        border-color 0.3s ease-in-out,
                        scale 3s ease-in-out;
          }
          
          .standings-list-container {
            transition: height 3s cubic-bezier(0.4, 0, 0.2, 1);
          }
        }
      `}</style>
      <h3 className="standings-heading" style={{ textAlign: 'center', color: headingColor }}>{t('gameplay.standings.title')}</h3>
      <div 
        ref={containerRef}
        className={`standings-list-container ${isMobile ? 'mobile' : ''}`}
        data-testid="standings-list-container"
        style={{ 
          display: 'flex',
          flexDirection: isMobile ? 'row' : 'column',
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          gap: isMobile ? '0.45rem' : '0.4rem',
          position: 'relative',
          paddingTop: isMobile ? '0.75rem' : '1rem',
        }}
      >
        {sortedPlayers.map((player, index) => {
          const rowSurface = getRowSurface(index);
          return isMobile ? (
            <div
              key={player.id}
              ref={(el) => {
                if (el) itemRefs.current.set(player.id, el);
                else itemRefs.current.delete(player.id);
              }}
              className="standings-player-item"
              data-testid="standings-player-item"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.4rem',
                padding: '0.42rem 0.42rem 0.42rem 0.58rem',
                borderRadius: '0.5rem',
                fontSize: '0.8rem',
                willChange: 'transform, opacity',
                color: nameColor,
                background: rowSurface.background,
                border: rowSurface.border,
                boxShadow: rowSurface.boxShadow,
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.32rem', minWidth: 0, flex: 1 }}>
                <span className={avatarZoomPlayerIds.has(player.id) ? 'standings-avatar-zoom' : ''} style={{ display: 'inline-flex', flexShrink: 0 }}>
                  {renderAvatar(player.avatar, '1.25rem')}
                </span>
                <span style={{ fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {player.name}
                </span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginLeft: 'auto', flexShrink: 0 }}>
                {showPointsGained.get(player.id) && pointsGained.get(player.id) && (
                  <span className="standings-points-gained standings-points-gained-pop" data-testid="standings-points-gained" style={{ 
                    background: 'transparent',
                    border: 'none',
                    borderRadius: 0,
                    padding: 0,
                    fontSize: '0.7rem',
                    fontWeight: 800,
                    lineHeight: 1.1,
                    boxShadow: 'none',
                  }}>
                    +{pointsGained.get(player.id)}{t('common.pts')}
                  </span>
                )}
                <span
                  ref={(el) => {
                    if (onScoreRef && el) {
                      onScoreRef(player.id, el);
                    }
                  }}
                  className="standings-score"
                  style={{
                    fontWeight: 800,
                    transition: 'color 0.3s ease-in-out',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: 0,
                    padding: 0,
                    minWidth: '3.25rem',
                    textAlign: 'center',
                    boxShadow: 'none',
                  }}
                >
                  <FlipRollingInteger from={previousScores.get(player.id)} to={player.score} />
                </span>
              </span>
            </div>
          ) : (
            <div
              key={player.id}
              ref={(el) => {
                if (el) itemRefs.current.set(player.id, el);
                else itemRefs.current.delete(player.id);
              }}
              className="standings-player-item standings-player-item-desktop"
              data-testid="standings-player-item"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.5rem',
                padding: '0.48rem 0.45rem 0.48rem 0.62rem',
                borderBottom: index < sortedPlayers.length - 1 ? rowDivider : 'none',
                willChange: 'transform, opacity',
                borderRadius: isLightCard ? '16px' : undefined,
                background: rowSurface.background,
                border: rowSurface.border,
                boxShadow: rowSurface.boxShadow,
                color: nameColor,
                marginBottom: index < sortedPlayers.length - 1 ? '0.22rem' : undefined,
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', minWidth: 0, flex: 1 }}>
                <span className={`standings-avatar ${avatarZoomPlayerIds.has(player.id) ? 'standings-avatar-zoom' : ''}`} style={{ flexShrink: 0 }}>
                  {renderAvatar(player.avatar, '1.55rem')}
                </span>
                <span className="standings-name" style={{ flex: 1, color: nameColor, fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {player.name}
                </span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto', flexShrink: 0 }}>
                {showPointsGained.get(player.id) && pointsGained.get(player.id) && (
                  <span className="standings-points-gained standings-points-gained-pop" data-testid="standings-points-gained" style={{ 
                    background: 'transparent',
                    border: 'none',
                    borderRadius: 0,
                    padding: 0,
                    fontWeight: 800,
                    lineHeight: 1.1,
                    boxShadow: 'none',
                  }}>
                    +{pointsGained.get(player.id)}{t('common.pts')}
                  </span>
                )}
                <span
                  ref={(el) => {
                    if (onScoreRef && el) {
                      onScoreRef(player.id, el);
                    }
                  }}
                  className="standings-score"
                  style={{
                    fontWeight: 800,
                    transition: 'color 0.3s ease-in-out',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: 0,
                    padding: 0,
                    minWidth: '3.7rem',
                    textAlign: 'center',
                    boxShadow: 'none',
                  }}
                >
                  <FlipRollingInteger from={previousScores.get(player.id)} to={player.score} />
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
